import { ServicesContent } from '@core/services/site-content/site-content.types';
import { Component, css, html, LitElement, property } from '@rxdi/lit-html';
import { rich } from '@shared/rich-text';
import { StudioStyles } from '@shared/styles/studio.styles';

/** Home: what we do. */
@Component({
  selector: 'rs-services',
  styles: [
    StudioStyles,
    css`
      .grid {
        display: grid;
        gap: 1px;
        background: var(--rs-line);
        border: 1px solid var(--rs-line);
        border-radius: var(--rs-radius-lg);
        overflow: hidden;
      }

      @media (min-width: 860px) {
        .grid {
          grid-template-columns: 1fr 1fr;
        }
      }

      .service {
        position: relative;
        padding: clamp(28px, 4vw, 48px);
        background: var(--rs-ink-2);
        overflow: hidden;
        transition: background 0.4s var(--rs-ease);
      }

      .service::after {
        content: '';
        position: absolute;
        width: 320px;
        height: 320px;
        right: -120px;
        top: -120px;
        border-radius: 50%;
        background: radial-gradient(circle, var(--accent-soft), transparent 70%);
        opacity: 0;
        transition: opacity 0.5s var(--rs-ease);
      }

      .service:hover {
        background: var(--rs-surface);
      }

      .service:hover::after {
        opacity: 1;
      }

      .service.signal {
        --accent: var(--rs-signal);
        --accent-soft: var(--rs-signal-soft);
      }
      .service.plasma {
        --accent: var(--rs-plasma);
        --accent-soft: var(--rs-plasma-soft);
      }
      .service.ion {
        --accent: var(--rs-ion);
        --accent-soft: var(--rs-ion-soft);
      }
      .service.ember {
        --accent: var(--rs-ember);
        --accent-soft: var(--rs-signal-soft);
      }

      .n {
        font-family: var(--rs-font-mono);
        font-size: 12px;
        color: var(--accent);
      }

      h3 {
        margin: 18px 0 12px;
        font-family: var(--rs-font-display);
        font-size: clamp(26px, 2.6vw, 34px);
        letter-spacing: -0.03em;
        line-height: 1.05;
      }

      p {
        margin: 0;
        max-width: 46ch;
        color: var(--rs-text-2);
        line-height: 1.6;
      }

      ul {
        list-style: none;
        margin: 28px 0 0;
        padding: 0;
        display: grid;
        gap: 10px;
      }

      li {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 15px;
        color: var(--rs-text);
      }

      li::before {
        content: '';
        width: 14px;
        height: 1px;
        background: var(--accent);
      }
    `,
  ],
  template(this: ServicesComponent) {
    const c = this.content;
    if (!c) {
      return html``;
    }
    return html`
      <section class="section" id="services">
        <div class="wrap">
          <div class="section-head split">
            <div>
              <span class="kicker"><span class="index">03</span> What we do</span>
              <h2 class="h2">${rich(c.heading)}</h2>
            </div>
            <p class="lead">${rich(c.lead)}</p>
          </div>

          <div class="grid">
            ${c.services.map(
              (s, i) => html`
                <article class="service ${s.accent}">
                  <span class="n">0${i + 1}</span>
                  <h3>${s.title}</h3>
                  <p>${s.body}</p>
                  <ul>
                    ${s.items.map((item) => html`<li>${item}</li>`)}
                  </ul>
                </article>
              `
            )}
          </div>
        </div>
      </section>
    `;
  },
})
export class ServicesComponent extends LitElement {
  @property({ attribute: false }) content: ServicesContent;
}
